// ToyCommandAnimationItem.jsx
import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { LRUCache } from 'lru-cache';
import { withTranslation } from 'react-i18next';
import getAPNG from '../resource/getAPNG.js';
import { color } from '../style/variables.js';
import useKaraokeTranslation from '../hook/useKaraokeTranslation.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import Marquee from './Marquee.jsx';
import { NULL_FUNCTION } from '../resource/defaults.js';
import DecryptionWrapper from '../container/DecryptionWrapper.js';
import Animator from './Animator.jsx';

const apngCache = new LRUCache({ max: 10 });

// Canvas display constants
const CANVAS_DISPLAY_WIDTH = 73;
const CANVAS_DISPLAY_HEIGHT = 18;

const ToyCommandAnimationItem = React.memo(
  ({
    isToy = false,
    isToyCommandAnimationStarted = false,
    startUnixTimestamp = null,
    senderId = null,
    senderUsername = null,
    productId = '',
    goalTitle = '',
    target = 0,
    preset,
    toyCommandImage = '',
    isFetchedLargeImage = false,
    fetchUser = NULL_FUNCTION,
    nextProductId = '',
    nextGoalTitle = '',
    nextIsToy = false,
    nextTarget = 0,
    t,
  }) => {
    const canvasRef = useRef();
    const playerRef = useRef(null);
    const displayTitle = useKaraokeTranslation({
      productId,
      title: goalTitle,
      isToy,
    });

    const nextDisplayTitle = useKaraokeTranslation({
      productId: nextProductId,
      title: nextGoalTitle,
      isToy: nextIsToy,
    });

    const nextCommandText =
      nextProductId && nextDisplayTitle
        ? `${nextDisplayTitle} ${t('command_sec', {
            number: nextTarget,
            ns: TranslationNamespace.LIVESTREAM,
          })}`
        : '';

    const themeColor = isToy ? color.lovense : color.yellow;

    // cleanup functions
    const cleanupAPNGPlayer = useCallback(() => {
      if (playerRef.current) {
        playerRef.current.stop();
        playerRef.current = null;
      }
    }, []);

    const cleanupCanvasContent = useCallback(() => {
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }, []);

    const cleanupAllResources = useCallback(() => {
      cleanupAPNGPlayer();
      cleanupCanvasContent();
    }, [cleanupAPNGPlayer, cleanupCanvasContent]);

    // Calculate countdown and progress function
    const calculateCountdown = useCallback(
      now => {
        if (
          !isToyCommandAnimationStarted ||
          !target ||
          target <= 0 ||
          !startUnixTimestamp
        ) {
          return { countdown: target, progress: 1 };
        }

        const startTime = startUnixTimestamp * 1000;
        const timePassed = now - startTime;
        const totalDurationInMs = target * 1000;

        if (timePassed >= totalDurationInMs) {
          // Animation completed
          return { countdown: 0, progress: 0 };
        }

        const currentProgress = 1 - timePassed / totalDurationInMs;
        const remainingSeconds = Math.ceil(currentProgress * target) || 1;

        return {
          countdown: remainingSeconds,
          progress: currentProgress,
        };
      },
      [isToyCommandAnimationStarted, target, startUnixTimestamp]
    );

    const playAPNGAnimation = useCallback(async () => {
      if (playerRef.current) {
        return;
      }

      cleanupAPNGPlayer();

      const imageUrl = toyCommandImage;
      if (!imageUrl) {
        return;
      }

      if (!canvasRef.current) {
        return;
      }
      // Then try to get the APNG image
      let apngData = apngCache.get(imageUrl);
      if (!apngData) {
        const { isAPNG, apng } = await getAPNG({
          imageSource: imageUrl,
        });
        apngData = { isAPNG, apng };
        apngCache.set(imageUrl, apngData);
      }

      const { isAPNG, apng } = apngData;

      if (!isAPNG || !apng) {
        return;
      }

      // Handle APNG animation
      const scale = CANVAS_DISPLAY_WIDTH / apng.width;
      const displayHeight = Math.round(apng.height * scale);

      // Clear the canvas and set the canvas size for APNG
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      canvasRef.current.width = apng.width;
      canvasRef.current.height = apng.height;
      canvasRef.current.style.width = `${CANVAS_DISPLAY_WIDTH}px`;
      canvasRef.current.style.height = `${displayHeight}px`;

      // Play the APNG animation
      try {
        const player = await apng.getPlayer(ctx);
        playerRef.current = player;
        player.play();
      } catch (error) {
        playerRef.current = null;
      }
    }, [toyCommandImage, cleanupAPNGPlayer]);

    // get the sender username
    useEffect(() => {
      if (senderId && !senderUsername) {
        fetchUser({ userId: senderId });
      }
    }, [senderId, senderUsername, fetchUser]);

    // play the apng animation
    useEffect(() => {
      if (
        isToyCommandAnimationStarted &&
        toyCommandImage &&
        toyCommandImage.trim() !== ''
      ) {
        playAPNGAnimation();
      }
    }, [isToyCommandAnimationStarted, toyCommandImage, playAPNGAnimation]);

    // clean up the resources when the component is unmounted
    useEffect(() => {
      return () => {
        cleanupAllResources();
      };
    }, [cleanupAllResources]);

    if (!isToyCommandAnimationStarted) {
      return null;
    }

    return (
      <StyledToyCommandAnimationItem>
        <Content>
          <Title themeColor={themeColor}>
            {displayTitle && displayTitle.length > 10 ? (
              <Marquee text={displayTitle} duration={8} justify={'center'} />
            ) : (
              displayTitle
            )}
          </Title>

          <AnimationWrapper>
            <CanvasWrapper>
              {isFetchedLargeImage && toyCommandImage ? (
                <GiftCanvas ref={canvasRef} />
              ) : (
                <DecryptionWrapper resourceUrl={toyCommandImage}>
                  <StyledStaticImage src={toyCommandImage} alt="" />
                </DecryptionWrapper>
              )}
            </CanvasWrapper>

            <Animator
              fps={10}
              render={(now, stopAnimator) => {
                const { countdown, progress } = calculateCountdown(now);

                if (countdown === 0) {
                  stopAnimator();
                }

                return (
                  <ProgressCircle preset={preset}>
                    <ProgressOuterRing
                      progress={progress}
                      themeColor={themeColor}
                    />
                    <ProgressBackground />
                    <ProgressNumber>{countdown}</ProgressNumber>
                  </ProgressCircle>
                );
              }}
            />
          </AnimationWrapper>
        </Content>

        <Username themeColor={themeColor}>
          {senderUsername &&
            `${t('general_by', { ns: TranslationNamespace.LIVESTREAM })} @${senderUsername}`}
        </Username>

        {nextCommandText && (
          <NextCommandText>
            {nextCommandText.length > 10 ? (
              <Marquee text={nextCommandText} duration={8} justify={'center'} />
            ) : (
              nextCommandText
            )}
          </NextCommandText>
        )}
      </StyledToyCommandAnimationItem>
    );
  }
);

ToyCommandAnimationItem.displayName = 'ToyCommandAnimationItem';

ToyCommandAnimationItem.propTypes = {
  isToy: PropTypes.bool,
  isToyCommandAnimationStarted: PropTypes.bool,
  startUnixTimestamp: PropTypes.number,
  senderId: PropTypes.string,
  senderUsername: PropTypes.string,
  productId: PropTypes.string,
  goalTitle: PropTypes.string,
  target: PropTypes.number,
  preset: PropTypes.string,
  toyCommandImage: PropTypes.string,
  isFetchedLargeImage: PropTypes.bool,
  fetchUser: PropTypes.func,
  nextProductId: PropTypes.string,
  nextGoalTitle: PropTypes.string,
  nextIsToy: PropTypes.bool,
  nextTarget: PropTypes.number,
  t: PropTypes.func.isRequired,
};

const StyledToyCommandAnimationItem = styled.div`
  width: 108px;
  background: ${color.neutral['0_alpha_80']};
  border-radius: 4px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 4px;
`;

const Content = styled.div`
  width: 102px;
  height: 44px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
`;

const Title = styled.div.attrs(props => ({
  style: { color: props.themeColor },
}))`
  width: 100%;
  max-width: 102px;
  font-weight: 600;
  font-size: 12px;
  line-height: 1.5em;
  text-align: center;
  overflow: hidden;
  height: 18px;
`;

const AnimationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const CanvasWrapper = styled.div`
  width: 73px;
  height: 18px;
  overflow: visible;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProgressCircle = styled.div`
  margin-left: -12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  position: relative;
  z-index: 1;
`;

const ProgressOuterRing = styled.div.attrs(props => ({
  style: {
    // Create circular progress indicator using conic gradient
    background: `conic-gradient(
      ${props.themeColor} 0deg,
      ${props.themeColor} ${props.progress * 360}deg,
      transparent ${props.progress * 360}deg,
      transparent 360deg
    )`,
  },
}))`
  position: absolute;
  top: 0;
  left: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;

  &::before {
    content: '';
    position: absolute;
    top: 1.5px;
    left: 1.5px;
    width: 21px;
    height: 21px;
    border-radius: 50%;
    background: ${color.neutral['0_alpha_80']};
  }
`;

const ProgressBackground = styled.div`
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${color.neutral['0_alpha_80']};
  border: 3px solid ${color.neutral['0_alpha_80']};
`;

const ProgressNumber = styled.div`
  position: absolute;
  top: 3px;
  left: 1px;
  width: 22px;
  height: 18px;
  color: ${color.white};
  font-weight: 600;
  font-size: 12px;
  line-height: 1.5em;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GiftCanvas = styled.canvas`
  object-fit: contain;
`;

const StyledStaticImage = styled.img`
  width: ${CANVAS_DISPLAY_WIDTH}px;
  height: ${CANVAS_DISPLAY_HEIGHT}px;
  object-fit: contain;
`;

const Username = styled.div.attrs(props => ({
  style: {
    color: props.themeColor,
  },
}))`
  width: 100%;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.5em;
  text-align: center;
  margin-top: 4px;
  overflow: hidden;
  height: 18px;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const NextCommandText = styled.div`
  width: 100%;
  color: ${color.neutral[30]};
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5em;
  letter-spacing: 0.012px;
  margin-top: 4px;
  overflow: hidden;
  height: 18px;
`;

export default withTranslation()(ToyCommandAnimationItem);
